import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, InputGroup, Form, ButtonGroup } from 'react-bootstrap';
import { FiMapPin, FiShield, FiHeadphones, FiMap, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { MdHome } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import QuickAccessButtons from '../components/QuickAccessButtons';
import LocationAutocomplete from '../components/LocationAutocomplete';
import { PropertyThumbnail } from '../components/OptimizedImage';
import './Home.css';

const Home = () => {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentImageIndices, setCurrentImageIndices] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedProperties();
  }, []);

  const fetchFeaturedProperties = async () => {
    try {
      const response = await api.get('/api/properties?per_page=8');
      setProperties(response.data.properties);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const query = selectedLocation?.mainText || searchQuery;
    if (query.trim()) {
      navigate(`/properties?q=${encodeURIComponent(query)}`);
    }
  };

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
    setSearchQuery(location.mainText);
  };

  const handleMapSearch = async (e) => {
    e.preventDefault();

    // If there's a selected location with placeId, fetch coordinates and navigate
    if (selectedLocation && selectedLocation.placeId) {
      try {
        const response = await api.get('/api/properties/place-details', {
          params: { place_id: selectedLocation.placeId }
        });

        if (response.data && response.data.geometry) {
          const { lat, lng } = response.data.geometry.location;
          const query = selectedLocation.mainText || searchQuery;
          navigate(`/map?place_id=${selectedLocation.placeId}&lat=${lat}&lng=${lng}&q=${encodeURIComponent(query)}`);
        } else {
          // Fallback: navigate with just place_id
          navigate(`/map?place_id=${selectedLocation.placeId}&q=${encodeURIComponent(selectedLocation.mainText)}`);
        }
      } catch (error) {
        console.error('Error fetching coordinates for map:', error);
        // Fallback: navigate to map without coordinates
        navigate('/map');
      }
    } else {
      // No location selected, just go to map (will center on Uberlândia)
      navigate('/map');
    }
  };

  const handlePrevImage = (e, propertyId, totalImages) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndices(prev => ({
      ...prev,
      [propertyId]: ((prev[propertyId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const handleNextImage = (e, propertyId, totalImages) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndices(prev => ({
      ...prev,
      [propertyId]: ((prev[propertyId] || 0) + 1) % totalImages
    }));
  };

  return (
    <div style={{ marginTop: '0' }}>
      {/* Hero Section */}
      <section className="position-relative overflow-hidden" style={{ height: '90vh' }}>
        <div className="position-absolute top-0 start-0 w-100 h-100" style={{ zIndex: 0 }}>
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-100 h-100"
            style={{ objectFit: 'cover' }}
          >
            <source src="/nukki-optimized.mp4" type="video/mp4" />
          </video>
        </div>
        <div
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{
            background: 'linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.3))',
            zIndex: 1
          }}
        />
        <Container
          fluid
          className="position-relative h-100 d-flex flex-column justify-content-center align-items-center text-white text-center"
          style={{ zIndex: 2, maxWidth: '1400px' }}
        >
          <h1
            className="display-3 fw-light mb-4"
            style={{ color: 'white' }}
          >
            {t('home.title')}
          </h1>
          <p
            className="fs-6 mb-5"
            style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)', maxWidth: '800px' }}
          >
            {t('home.subtitle')}
          </p>

          <Form onSubmit={handleSearch} className="w-100" style={{ maxWidth: '600px' }}>
            <InputGroup size="lg" className="shadow-lg flex-nowrap">
              <LocationAutocomplete
                value={searchQuery}
                onChange={handleLocationChange}
                placeholder={t('home.searchPlaceholder')}
              />
              <ButtonGroup>
                <Button
                  type="submit"
                  variant="primary"
                  className="px-4"
                >
                  {t('home.search')}
                </Button>
                <Button
                  onClick={handleMapSearch}
                  variant="secondary"
                  className="px-3"
                  title={t('common.viewOnMap')}
                >
                  <FiMap size={20} />
                </Button>
              </ButtonGroup>
            </InputGroup>
          </Form>

          {/* Quick Access Badges */}
          <div className="mt-5 w-100">
            <QuickAccessButtons />
          </div>
        </Container>
      </section>

      {/* Featured Properties */}
      <section className="py-5 bg-light">
        <Container fluid style={{ maxWidth: '1400px' }}>
          <h2 className="h1 fw-bold text-center mb-5">{t('home.featuredProperties')}</h2>
          <Row xs={1} sm={2} lg={3} xl={4} className="g-4">
            {properties.map((property) => {
              const currentImageIndex = currentImageIndices[property.id] || 0;
              const hasMultipleImages = property.images && property.images.length > 1;
              const currentImage = property.images && property.images.length > 0
                ? property.images[currentImageIndex]?.image_url
                : null;

              return (
                <Col key={property.id}>
                  <Link to={`/properties/${property.id}`} className="text-decoration-none">
                    <Card className="h-100 border-0 shadow-sm property-card">
                      <div className="position-relative property-image-container" style={{ height: '250px', overflow: 'hidden' }}>
                        {currentImage ? (
                          <>
                            <PropertyThumbnail
                              src={currentImage}
                              width={400}
                              height={300}
                              alt={property.title}
                              className="w-100 h-100"
                              style={{ objectFit: 'cover' }}
                            />
                            {hasMultipleImages && (
                              <>
                                <button
                                  className="image-nav-button image-nav-left"
                                  onClick={(e) => handlePrevImage(e, property.id, property.images.length)}
                                  aria-label={t('common.previousImage')}
                                >
                                  <FiChevronLeft size={24} />
                                </button>
                                <button
                                  className="image-nav-button image-nav-right"
                                  onClick={(e) => handleNextImage(e, property.id, property.images.length)}
                                  aria-label={t('common.nextImage')}
                                >
                                  <FiChevronRight size={24} />
                                </button>
                                <div className="image-dots">
                                  {property.images.map((_, index) => (
                                    <span
                                      key={index}
                                      className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                                    />
                                  ))}
                                </div>
                              </>
                            )}
                          </>
                        ) : (
                          <div className="w-100 h-100 bg-secondary bg-opacity-25 d-flex align-items-center justify-content-center text-muted">
                            {t('home.noImage')}
                          </div>
                        )}
                      </div>
                    <Card.Body>
                      <div className="d-flex align-items-center text-muted mb-2 small">
                        <FiMapPin size={14} className="me-1" />
                        {property.city}, {property.country}
                      </div>
                      <Card.Title className="h6 mb-2">{property.title}</Card.Title>
                      <div className="text-muted mb-2 small">
                        <span>{property.bedrooms} {property.bedrooms === 1 ? t('property.bed') : t('property.beds')}</span>
                        <span className="mx-1">·</span>
                        <span>{property.bathrooms} {property.bathrooms === 1 ? t('property.bath') : t('property.baths')}</span>
                        <span className="mx-1">·</span>
                        <span>{property.max_guests} {property.max_guests === 1 ? t('property.guest') : t('property.guests')}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <span>⭐ {property.average_rating.toFixed(1)}</span>
                        </div>
                        <div>
                          <span className="fw-bold">R$ {property.price_per_night}</span>
                          <span className="text-muted small"> {t('home.perNight')}</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
              );
            })}
          </Row>
          <div className="text-center mt-5">
            <Button as={Link} to="/properties" variant="outline-primary" size="lg">
              {t('home.viewAll')}
            </Button>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section
        className="py-5"
        style={{
          background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)'
        }}
      >
        <Container fluid style={{ maxWidth: '1400px' }}>
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">{t('home.whyChoose')}</h2>
            <p className="text-muted fs-5" style={{ maxWidth: '600px', margin: '0 auto' }}>
              Conectamos você com experiências únicas de hospedagem
            </p>
          </div>
          <Row xs={1} md={3} className="g-4">
            <Col>
              <Card
                className="text-center border-0 h-100"
                style={{
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.07)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.07)';
                }}
              >
                <Card.Body className="p-5">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-4"
                    style={{
                      width: '80px',
                      height: '80px',
                      background: '#254544',
                      color: 'white'
                    }}
                  >
                    <MdHome size={40} />
                  </div>
                  <Card.Title className="h4 fw-bold mb-3">{t('home.feature1Title')}</Card.Title>
                  <Card.Text className="text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.6' }}>
                    {t('home.feature1Desc')}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card
                className="text-center border-0 h-100"
                style={{
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.07)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.07)';
                }}
              >
                <Card.Body className="p-5">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-4"
                    style={{
                      width: '80px',
                      height: '80px',
                      background: '#254544',
                      color: 'white'
                    }}
                  >
                    <FiShield size={40} strokeWidth={2} />
                  </div>
                  <Card.Title className="h4 fw-bold mb-3">{t('home.feature2Title')}</Card.Title>
                  <Card.Text className="text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.6' }}>
                    {t('home.feature2Desc')}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card
                className="text-center border-0 h-100"
                style={{
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.07)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.07)';
                }}
              >
                <Card.Body className="p-5">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-4"
                    style={{
                      width: '80px',
                      height: '80px',
                      background: '#254544',
                      color: 'white'
                    }}
                  >
                    <FiHeadphones size={40} strokeWidth={2} />
                  </div>
                  <Card.Title className="h4 fw-bold mb-3">{t('home.feature3Title')}</Card.Title>
                  <Card.Text className="text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.6' }}>
                    {t('home.feature3Desc')}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5 bg-nukki-secondary text-white">
        <Container fluid style={{ maxWidth: '1400px' }}>
          <div className="text-center py-5">
            <h2 className="display-5 fw-bold mb-4 text-white">{t('home.readyToHost')}</h2>
            <p className="fs-5 mb-4" style={{ maxWidth: '700px', margin: '0 auto 2rem' }}>
              {t('home.hostCta')}
            </p>
            <Button as={Link} to="/register" variant="light" size="lg" className="px-5">
              {t('home.becomeHost')}
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Home;
